import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PresaleSheetConfig,
  UpsertPresaleSheetConfigPayload,
  presaleSheetConfigApi,
} from '../api/presaleSheetApi';

const configQueryKey = (warrantyUuid: string | null) => ['presale-sheet-config', warrantyUuid];
const pdfQueryKeyPrefix = (warrantyUuid: string | null) => ['presale-sheet-pdf', warrantyUuid];

const assertWarrantyUuid = (warrantyUuid: string | null): string => {
  if (!warrantyUuid) {
    throw new Error('warrantyUuid is required');
  }
  return warrantyUuid;
};

/**
 * Lit la configuration persistee (ou les defauts si aucune ligne en BDD).
 */
export const usePresaleSheetConfig = (warrantyUuid: string | null) => {
  return useQuery<PresaleSheetConfig>({
    queryKey: configQueryKey(warrantyUuid),
    queryFn: () => presaleSheetConfigApi.get(assertWarrantyUuid(warrantyUuid)),
    enabled: !!warrantyUuid,
    staleTime: 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });
};

/**
 * Helper interne : applique une nouvelle config dans le cache et invalide
 * la query du PDF pour declencher une regeneration.
 */
const applyConfigToCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  warrantyUuid: string,
  data: PresaleSheetConfig,
) => {
  queryClient.setQueryData(configQueryKey(warrantyUuid), data);
  queryClient.invalidateQueries({ queryKey: pdfQueryKeyPrefix(warrantyUuid) });
};

/**
 * Mutation d'upsert (auto-save apres debounce cote composant).
 */
export const useUpsertPresaleSheetConfig = (warrantyUuid: string | null) => {
  const queryClient = useQueryClient();
  return useMutation<PresaleSheetConfig, Error, UpsertPresaleSheetConfigPayload>({
    mutationFn: (payload) => presaleSheetConfigApi.upsert(assertWarrantyUuid(warrantyUuid), payload),
    onSuccess: (data) => {
      if (warrantyUuid) applyConfigToCache(queryClient, warrantyUuid, data);
    },
  });
};

/**
 * Upload de l'image d'en-tete vers S3 + persistance de l'URL en BDD.
 * Resout aussi le 413 puisqu'on n'envoie plus de base64.
 */
export const useUploadPresaleHeaderImage = (warrantyUuid: string | null) => {
  const queryClient = useQueryClient();
  return useMutation<PresaleSheetConfig, Error, File>({
    mutationFn: (file) => presaleSheetConfigApi.uploadHeaderImage(assertWarrantyUuid(warrantyUuid), file),
    onSuccess: (data) => {
      if (warrantyUuid) applyConfigToCache(queryClient, warrantyUuid, data);
    },
  });
};

/**
 * Suppression de l'image personnalisee (S3 best-effort cote API) +
 * reset de `headerImageUrl` a null.
 */
export const useDeletePresaleHeaderImage = (warrantyUuid: string | null) => {
  const queryClient = useQueryClient();
  return useMutation<PresaleSheetConfig, Error, void>({
    mutationFn: () => presaleSheetConfigApi.deleteHeaderImage(assertWarrantyUuid(warrantyUuid)),
    onSuccess: (data) => {
      if (warrantyUuid) applyConfigToCache(queryClient, warrantyUuid, data);
    },
  });
};
